import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Package, Printer, Clock, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, openLabels } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MoveForm } from "@/components/ItemActions";
import { BottleChips } from "@/components/BottleChips";
import { BottleDetailsDialog } from "@/components/BottleDetailsDialog";
import { SelectionBar, toggleIn } from "@/components/SelectionBar";

export const boxLabelOf = (b) => ({ barcode: b.barcode, kind: "box", box_number: b.box_number, title: `Box ${b.box_number}` });

export default function BoxContents() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [moving, setMoving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [picked, setPicked] = useState([]);
  const load = useCallback(() => api.get(`/boxes/${encodeURIComponent(code)}/contents`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))), [code]);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="p-10 text-[#A89F91]" data-testid="box-error">{error}</p>;
  if (!data) return null;
  const move = async (location_id) => {
    try {
      await api.post("/moves", { barcode: data.barcode, location_id });
      toast.success("Box moved");
      setMoving(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const removeBox = async () => {
    if (!window.confirm(`Delete empty box ${data.box_number}?`)) return;
    try {
      await api.delete(`/boxes/${data.id}`);
      toast.success("Box deleted");
      navigate("/boxes");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const printBottles = () => openLabels(data.wines.flatMap((w) => w.bottles.filter((b) => picked.includes(b.barcode)).map((b) => ({ barcode: b.barcode, title: w.name, wine: `${w.name} ${w.vintage || "NV"}` }))), navigate);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="box-contents-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#A89F91] hover:text-[#D4AF37] mb-6" data-testid="box-back-button"><ArrowLeft className="w-4 h-4" />Back</button>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">{data.barcode}</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] flex items-center gap-3" data-testid="box-title"><Package className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.3} />Box {data.box_number}</h2>
      <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-sm text-[#A89F91]">
        <p data-testid="box-location"><MapPin className="w-4 h-4 inline mr-1.5 text-[#D4AF37]" />{data.location || "Unassigned"}</p>
        <p data-testid="box-bottle-count">{data.bottle_count} bottle{data.bottle_count === 1 ? "" : "s"}</p>
        <p data-testid="box-last-change"><Clock className="w-4 h-4 inline mr-1.5 text-[#D4AF37]" />Last change: {fmtDateTime(data.last_change)}</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-6 mb-8">
        <Button variant="outline" onClick={() => setMoving(true)} data-testid="box-move-button"><MapPin className="w-4 h-4 mr-2" />Change location</Button>
        <Button variant="outline" onClick={() => openLabels([boxLabelOf(data)], navigate)} data-testid="box-print-label-button"><Printer className="w-4 h-4 mr-2" />Print box label</Button>
        {!data.bottle_count && user?.role === "owner" && <Button variant="outline" onClick={removeBox} className="border-[#8C2B36]" data-testid="box-delete-button"><Trash2 className="w-4 h-4 mr-2" />Delete empty box</Button>}
      </div>
      {!!data.bottle_count && <SelectionBar count={picked.length} onPrint={printBottles} onClear={() => setPicked([])} />}
      <div className="space-y-4" data-testid="box-wines">
        {data.wines.map((w) => (
          <div key={w.wine_id} className="rounded-xl border border-[#2E252A] bg-[#161214] p-5" data-testid={`box-wine-${w.wine_id}`}>
            <button onClick={() => navigate(`/wines/${w.wine_id}`)} className="text-left">
              <p className="font-serif text-xl text-[#F5F0EB] hover:text-[#D4AF37]">{w.vintage || "NV"} {w.name}</p>
              <p className="text-sm text-[#A89F91]">{w.producer} · {w.bottles.length} bottle{w.bottles.length === 1 ? "" : "s"}</p>
            </button>
            <BottleChips bottles={w.bottles.map((b) => ({ ...b, box_number: null }))} onEdit={setEditing} selected={picked} onToggle={toggleIn(setPicked)} max={500} />
          </div>
        ))}
        {!data.wines.length && <p className="text-[#6E665D] py-10 text-center" data-testid="box-empty">This box is empty.</p>}
      </div>
      <Dialog open={moving} onOpenChange={setMoving}>
        <DialogContent className="max-w-md bg-[#161214] border-[#2E252A]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Move box {data.box_number}</DialogTitle></DialogHeader>
          <MoveForm onSave={move} />
        </DialogContent>
      </Dialog>
      {editing && <BottleDetailsDialog bottles={[editing]} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
